import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  deleteDocumentRequest,
  downloadDocumentRequest,
  downloadMultipleDocumentRequest,
  getDocumentByIdRequest,
  getDocumentsRequest,
  syncExpressoDocumentRequest,
  updateEmployeeOnDocumentRequest,
  updateStudentDeclarationRequest,
  uploadDocumentsRequest,
  uploadStudentInscriptionRequest,
} from 'api/requests/documents-requests';
import { DocumentToSend } from 'modules/documents/types';
import {
  DOCUMENTS__DELETE_DOCUMENT,
  DOCUMENTS__DELETE_DOCUMENT_ERROR,
  DOCUMENTS__DOWNLOAD_DOCUMENT,
  DOCUMENTS__DOWNLOAD_DOCUMENT_ERROR,
  DOCUMENTS__DOWNLOAD_EXPRESSO_DOCUMENT,
  DOCUMENTS__DOWNLOAD_MULTIPLE_DOCUMENT_ERROR,
  DOCUMENTS__GENERAL_LOADING,
  DOCUMENTS__GET_DOCUMENT_BY_ID,
  DOCUMENTS__GET_DOCUMENTS,
  DOCUMENTS__GET_DOCUMENTS_ERROR,
  DOCUMENTS__GET_DOCUMENTS_PAYLOAD,
  DOCUMENTS__GET_EMPLOYEE_DOCUMENTS_PAYLOAD,
  DOCUMENTS__SAVE_DOCUMENTS,
  DOCUMENTS__SAVE_DOCUMENTS_ERROR,
  DOCUMENTS__SET_PAGINATION,
  DOCUMENTS__UPDATE_EMPLOYEE_ON_DOCUMENT,
  DOCUMENTS__UPDATE_STUDENT_DECLARATION,
  DOCUMENTS__UPLOAD_STUDENT_DECLARATION,
} from 'store/constants';
import { IDocument } from 'types/documents';
import logError from 'utils/log-error';

export const setDocumentsRequestsLoadingAction = createAction<boolean>(DOCUMENTS__GENERAL_LOADING);

export const getDocumentsPayloadAction = createAction<IDocument[]>(DOCUMENTS__GET_DOCUMENTS_PAYLOAD);
export const getEmployeeDocumentsPayloadAction = createAction<IDocument[]>(DOCUMENTS__GET_EMPLOYEE_DOCUMENTS_PAYLOAD);
export const setPaginationAction = createAction<{
  totalItems: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}>(DOCUMENTS__SET_PAGINATION);
export const getDocumentsErrorAction = createAction<boolean>(DOCUMENTS__GET_DOCUMENTS_ERROR);

export const getDocumentsAction = createAsyncThunk(
  DOCUMENTS__GET_DOCUMENTS,
  async (
    props: {
      companyId: number;
      category: number | null;
      employeeId?: number;
      startDate: Date | null;
      endDate: Date | null;
      searchQuery?: string;
      triggerEmployeeDocuments?: boolean;
      // Pagination parameters
      page?: number;
      pageSize?: number;
      // Sorting parameters
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
    },
    thunkApi,
  ) => {
    thunkApi.dispatch(setDocumentsRequestsLoadingAction(true));
    thunkApi.dispatch(getDocumentsErrorAction(false));
    try {
      const response = await getDocumentsRequest({
        companyId: props?.companyId,
        category: props.category,
        employeeId: props?.employeeId,
        startDate: props.startDate,
        endDate: props.endDate,
        searchQuery: props.searchQuery,
        page: props.page,
        pageSize: props.pageSize,
        sortBy: props.sortBy,
        sortOrder: props.sortOrder,
      });

      thunkApi.dispatch(getDocumentsErrorAction(false));

      // Store pagination metadata
      thunkApi.dispatch(
        setPaginationAction({
          totalItems: response.numberOfResults,
          totalPages: response.totalPages,
          currentPage: response.pageIndex,
          pageSize: response.pageSize,
        }),
      );

      // Store documents
      if (props.triggerEmployeeDocuments) {
        thunkApi.dispatch(getEmployeeDocumentsPayloadAction(response.items));
      } else {
        thunkApi.dispatch(getDocumentsPayloadAction(response.items));
      }
    } catch (error) {
      thunkApi.dispatch(getDocumentsErrorAction(true));
    }
    thunkApi.dispatch(setDocumentsRequestsLoadingAction(false));
  },
);

export const saveDocumentsErrorAction = createAction<boolean>(DOCUMENTS__SAVE_DOCUMENTS_ERROR);

export const saveDocumentsAction = createAsyncThunk(
  DOCUMENTS__SAVE_DOCUMENTS,
  async (
    props: {
      companyId: number;
      documents: DocumentToSend[];
      page?: number;
      pageSize?: number;
      category?: number | null;
      employeeId?: number;
      startDate?: Date | null;
      endDate?: Date | null;
      searchQuery?: string;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
    },
    thunkApi,
  ) => {
    thunkApi.dispatch(setDocumentsRequestsLoadingAction(true));
    try {
      await uploadDocumentsRequest({
        companyId: props.companyId,
        documents: props.documents,
      });

      thunkApi.dispatch(saveDocumentsErrorAction(false));

      const page = props.page ?? 1;
      const pageSize = props.pageSize ?? 25;

      thunkApi.dispatch(
        getDocumentsAction({
          companyId: props.companyId,
          category: props.category ?? null,
          employeeId: props.employeeId,
          startDate: props.startDate ?? null,
          endDate: props.endDate ?? null,
          searchQuery: props.searchQuery,
          page,
          pageSize,
          sortBy: props.sortBy,
          sortOrder: props.sortOrder ?? 'asc',
        }),
      );
    } catch (error) {
      thunkApi.dispatch(saveDocumentsErrorAction(true));
    }
    thunkApi.dispatch(setDocumentsRequestsLoadingAction(false));
  },
);

export const uploadStudentInscription = createAsyncThunk(
  DOCUMENTS__UPLOAD_STUDENT_DECLARATION,
  async (
    props: {
      companyGuid: string;
      document: DocumentToSend;
    },
    thunkApi,
  ) => {
    thunkApi.dispatch(setDocumentsRequestsLoadingAction(true));
    try {
      await uploadStudentInscriptionRequest({
        companyGuid: props.companyGuid,
        documents: props.document,
      });

      thunkApi.dispatch(saveDocumentsErrorAction(false));
    } catch (error) {
      thunkApi.dispatch(saveDocumentsErrorAction(true));
    }
    thunkApi.dispatch(setDocumentsRequestsLoadingAction(false));
  },
);

export const updateStudentDeclarationAction = createAsyncThunk(
  DOCUMENTS__UPDATE_STUDENT_DECLARATION,
  async (
    props: {
      companyId: number;
      employeeId: number;
      academicPeriodStart: Date;
      academicPeriodEnd: Date;
    },
    thunkApi,
  ) => {
    const { companyId, employeeId, academicPeriodStart, academicPeriodEnd } = props;
    thunkApi.dispatch(setDocumentsRequestsLoadingAction(true));
    try {
      await updateStudentDeclarationRequest({
        companyId,
        employeeId,
        startDate: academicPeriodStart,
        endDate: academicPeriodEnd,
      });
      thunkApi.dispatch(saveDocumentsErrorAction(false));
    } catch (error) {
      thunkApi.dispatch(saveDocumentsErrorAction(true));
    }
    thunkApi.dispatch(setDocumentsRequestsLoadingAction(false));
  },
);

export const deleteDocumentErrorAction = createAction<boolean>(DOCUMENTS__DELETE_DOCUMENT_ERROR);

export const deleteDocumentAction = createAsyncThunk(
  DOCUMENTS__DELETE_DOCUMENT,
  async (
    props: {
      companyId: number;
      id: number;
      category: number | null;
      startDate: Date | null;
      endDate: Date | null;
      employeeId?: number;
      searchQuery?: string;
      // Pagination parameters to persist current view
      page?: number;
      pageSize?: number;
      // Sorting parameters
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
    },
    thunkApi,
  ) => {
    thunkApi.dispatch(setDocumentsRequestsLoadingAction(true));
    try {
      await deleteDocumentRequest({
        companyId: props.companyId,
        id: props.id,
      });

      thunkApi.dispatch(deleteDocumentErrorAction(false));
      thunkApi.dispatch(
        getDocumentsAction({
          companyId: props.companyId,
          category: props.category,
          employeeId: props.employeeId,
          startDate: props.startDate,
          endDate: props.endDate,
          searchQuery: props.searchQuery,
          page: props.page ?? 1,
          pageSize: props.pageSize ?? 25,
          sortBy: props.sortBy,
          sortOrder: props.sortOrder ?? 'asc',
        }),
      );
    } catch (error) {
      thunkApi.dispatch(deleteDocumentErrorAction(true));
    }
    thunkApi.dispatch(setDocumentsRequestsLoadingAction(false));
  },
);

export const downloadDocumentErrorAction = createAction<boolean>(DOCUMENTS__DOWNLOAD_DOCUMENT_ERROR);

export const downloadDocumentAction = createAsyncThunk(
  DOCUMENTS__DOWNLOAD_DOCUMENT,
  async (props: { companyId: number; id: number; name: string }, thunkApi) => {
    thunkApi.dispatch(setDocumentsRequestsLoadingAction(true));
    try {
      const response = await downloadDocumentRequest({
        companyId: props.companyId,
        id: props.id,
      });

      const url = window.URL.createObjectURL(new Blob([response.blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', response.name !== 'default-filename' ? response.name : props.name);
      document.body.appendChild(link);
      link.click();

      thunkApi.dispatch(downloadDocumentErrorAction(false));
    } catch (error) {
      thunkApi.dispatch(downloadDocumentErrorAction(true));
    }
    thunkApi.dispatch(setDocumentsRequestsLoadingAction(false));
  },
);

export const downloadMultipleDocumentErrorAction = createAction<boolean>(DOCUMENTS__DOWNLOAD_MULTIPLE_DOCUMENT_ERROR);

export const downloadMultipleDocumentAction = createAsyncThunk(
  DOCUMENTS__DOWNLOAD_DOCUMENT,
  async (
    props: {
      companyId: number;
      zipName: string;
      // Option 1: Specific IDs
      ids?: number[];
      // Option 2: Select all with exclusions
      selectAll?: boolean;
      excludedIds?: number[];
      filters?: {
        category?: number | null;
        employeeId?: number | null;
        startDate?: Date | null;
        endDate?: Date | null;
        searchQuery?: string;
      };
    },
    thunkApi,
  ) => {
    thunkApi.dispatch(setDocumentsRequestsLoadingAction(true));
    try {
      const response = await downloadMultipleDocumentRequest({
        companyId: props.companyId,
        ids: props.ids,
        selectAll: props.selectAll,
        excludedIds: props.excludedIds,
        filters: props.filters,
      });

      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', props.zipName);
      document.body.appendChild(link);
      link.click();

      thunkApi.dispatch(downloadMultipleDocumentErrorAction(false));
    } catch (error) {
      thunkApi.dispatch(downloadMultipleDocumentErrorAction(true));
    }
    thunkApi.dispatch(setDocumentsRequestsLoadingAction(false));
  },
);

export const syncExpressoInvoicesErrorAction = createAction<boolean>(DOCUMENTS__DOWNLOAD_DOCUMENT_ERROR);

export const syncExpressoInvoicesAction = createAsyncThunk(
  DOCUMENTS__DOWNLOAD_EXPRESSO_DOCUMENT,
  async (props: { companyId: number; startDate: Date; endDate: Date; addEmployeeDocs: boolean }, thunkApi) => {
    thunkApi.dispatch(setDocumentsRequestsLoadingAction(true));
    try {
      await syncExpressoDocumentRequest({
        companyId: props.companyId,
        startDate: props.startDate,
        endDate: props.endDate,
        addEmployeeDocs: props.addEmployeeDocs,
      });

      thunkApi.dispatch(downloadDocumentErrorAction(false));
    } catch (error) {
      thunkApi.dispatch(downloadDocumentErrorAction(true));
    }
    thunkApi.dispatch(setDocumentsRequestsLoadingAction(false));
  },
);

export const checkDocumentExistsAction = createAsyncThunk(
  DOCUMENTS__GET_DOCUMENT_BY_ID,
  async (props: { companyId: number; id: number }, thunkApi) => {
    try {
      await getDocumentByIdRequest({ companyId: props.companyId, id: props.id });
      return true;
    } catch (error) {
      const status = axios.isAxiosError(error) && error.response ? error.response.status : 0;
      return thunkApi.rejectWithValue(status);
    }
  },
);

export const updateEmployeeOnDocumentAction = createAsyncThunk(
  DOCUMENTS__UPDATE_EMPLOYEE_ON_DOCUMENT,
  async (props: { documentId: number; employeeId: number; companyId: number }, thunkApi) => {
    thunkApi.dispatch(setDocumentsRequestsLoadingAction(true));
    try {
      await updateEmployeeOnDocumentRequest({
        documentId: props.documentId,
        employeeId: props.employeeId,
        companyId: props.companyId,
      });
    } catch (error) {
      logError(error);
    }
    thunkApi.dispatch(setDocumentsRequestsLoadingAction(false));
  },
);
